import { FC, MouseEvent, ReactNode } from 'react';
import { Link, LinkProps } from '@material-ui/core';

import useOpenRecord from 'hooks/useOpenRecord';
import { buildRecordPath, TabRecordType } from 'constants/tabs';

interface Props extends Omit<LinkProps, 'href' | 'onClick'> {
  recordType: TabRecordType;
  recordId: string | number;
  /** Record name, used as the tab label for types that are not labelled by id (client). */
  recordLabel?: string;
  /** Side effect the old handler carried, such as closing the menu the link sits in. */
  onOpen?: () => void;
  children: ReactNode;
}

/**
 * A blue link to a record.
 *
 * It renders a real anchor with a real href, which is the whole trick behind two acceptance criteria
 * at once: Ctrl+click and Cmd+click still open a browser tab, and the browser's own right-click menu
 * still offers "Open in new tab" (TAB-02). Neither needs a line of code here — they only need us to
 * stop hijacking plain clicks with a button.
 */
const RecordLink: FC<Props> = props => {
  const openRecord = useOpenRecord();
  const { recordType, recordId, recordLabel, onOpen, children, ...linkProps } = props;

  const href = buildRecordPath(recordType, recordId);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Let the browser have the click when the user asked for a browser behaviour.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    openRecord({ recordType, recordId, label: recordLabel });

    if (onOpen) {
      onOpen();
    }
  };

  return (
    <Link color='primary' {...linkProps} href={href} onClick={handleClick}>
      {children}
    </Link>
  );
};

export default RecordLink;
